<template>
  <template v-if="showCommentForm">
    <comment-form
      :thoughtid="thoughtid"
      :topicid="topicid"
      :comment="comment"
      @comment-form-close="onCommentFormClose()"
      @comment-updated="updatedMyComment"
    ></comment-form>
  </template>
  <div v-if="!showCommentForm" :id="commentIdTag(comment.commentid)" :class="commentClasses">
    <div class="row">
      <div class="col-md-12 clearfix">
        <avatar
          :user="comment.user ? comment.user : {}"
          :anon="comment.user ? false : true"
          :context_obj="comment"
          context_type="comment"
          :avatar_size="36"
          :enable_edit="canEditComment(comment)"
          @edit="editComment()"
          :show_status_label="isOwnComment || user.type == 'admin'"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="comment-content" v-dompurify-html="commentContentText"></div>
      </div>
      <attachments v-if="comment.files && comment.files.length > 0" :files="comment.files" />

      <comment-support v-if="!isOwnComment" :comment="comment" :topicid="topicid" />
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { toRefs, computed, ref, onBeforeMount, onMounted } from 'vue';

import useTopicConfig from '@/composables/topic/useTopicConfig';
import useCommentEvents from '@/composables/comment/useCommentEvents';

import Avatar from '@/components/discuss/general/Avatar.vue';
import Attachments from '@/components/general/Attachments.vue';
import CommentSupport from '@/components/discuss/comment/CommentSupport.vue';
import CommentForm from '@/components/discuss/comment/CommentForm.vue';

export default {
  name: 'comment',

  props: {
    comment: {
      required: true,
      type: Object,
    },
    topicid: {
      required: true,
      type: Number,
    },
    thoughtid: {
      required: false,
      type: Number,
    },
  },
  emits: [
    'comment-form-open',
    'comment-form-close',
    'comment-user-has-own',
    'comment-updated',
    'comment-vote-sent',
    'commentlist-has-own',
  ],

  setup(props, context) {
    const { comment } = toRefs(props);
    const { canEditComment } = useTopicConfig();
    const { emitCommentFormClose, emitCommentUpdated } = useCommentEvents(context);
    const showCommentForm = ref(false);
    const commentContent = ref('');
    // Computed
    const user = computed(() => {
      return userStore.getState().user;
    });

    const isOwnComment = computed(() => {
      if (user.value && !user.value.virtual) {
        return user.value.id == comment.value.user.id ? true : false;
      }
      return false;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const commentClasses = computed(() => {
      return {
        highlighted: comment.value.highlight || isOwnComment.value, // always highlight "my-comment"
        'wdw-comment': true,
        'wdw-my-comment': isOwnComment.value,
      };
    });
    const commentContentText = computed(() => {
      return commentContent.value.replaceAll('\n', '<br/>');
    });
    // METHODS
    function commentIdTag(commentId) {
      return `comment-${commentId}`;
    }
    function editComment() {
      showCommentForm.value = true;
    }

    function onCommentFormClose() {
      showCommentForm.value = false;
      emitCommentFormClose();
    }

    function updatedMyComment(newComment) {
      showCommentForm.value = false;
      emitCommentFormClose();
      emitCommentUpdated(newComment);
      commentContent.value = newComment.content;
    }
    onMounted(() => {
      commentContent.value = comment.value.content;
    });

    return {
      //vars
      showCommentForm,
      // methods
      commentIdTag,
      editComment,
      canEditComment,
      onCommentFormClose,
      updatedMyComment,
      // computed
      isOwnComment,
      user,
      isLoggedIn,
      commentClasses,
      commentContentText,
      commentContent,
    };
  },

  components: {
    Avatar,
    CommentSupport,
    CommentForm,
    Attachments,
  },
};
</script>
<style lang="scss" scoped>
.wdw-comment {
  padding: 15px;
}
</style>
